import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../hooks/usePermissions';
import { PageKey } from '../../types/permissions';
import { findGroupForView } from './navGroups';

interface ContentTabsProps {
  currentView: string;
  onTabChange: (viewId: string) => void;
  currentUser?: any;
  orgId?: string;
}

/**
 * Second-level horizontal navigation. Given the active view, it renders the
 * sibling sub-views of that view's group as tabs. Groups with a single view
 * (Dashboard, Guide) render nothing. Tabs the user may not access are hidden
 * (mirrors the sidebar's permission rules).
 */
const ContentTabs: React.FC<ContentTabsProps> = ({ currentView, onTabChange, currentUser, orgId }) => {
  const { permissions, canRead } = usePermissions(currentUser, orgId);
  const { t } = useTranslation('navigation');

  // Views that are always reachable regardless of role (match SidebarNew).
  const alwaysVisible = useMemo(() => new Set(['feedback', 'guide', 'ai-insights', 'quick-select', 'meeting-prep']), []);

  const group = findGroupForView(currentView);

  const visibleItems = useMemo(() => {
    if (!group) return [];
    return group.items.filter(item => {
      if (alwaysVisible.has(item.id)) return true;
      return permissions ? canRead(item.id as PageKey) : true;
    });
  }, [group, permissions, canRead, alwaysVisible]);

  // No tabs for single-view groups or when nothing (extra) is accessible.
  if (!group || visibleItems.length <= 1) return null;

  return (
    <div className="mb-5 -mx-1 flex flex-wrap items-center gap-1 border-b border-default">
      {visibleItems.map(item => {
        const isActive = item.id === currentView;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onTabChange(item.id)}
            className={`relative px-3.5 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              isActive
                ? 'text-accent'
                : 'text-txt-tertiary hover:text-txt-secondary hover:bg-bg-surface'
            }`}
          >
            {item.i18nKey ? t(item.i18nKey, { defaultValue: item.label }) : item.label}
            {isActive && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-accent" />}
          </button>
        );
      })}
    </div>
  );
};

export default ContentTabs;
